import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { setLanguage } from '../store/languageSlice';
import { Menu, X, User, LogOut } from 'lucide-react';
import { RootState, AppDispatch } from '../store';
import { logout } from '../store/authSlice';
import logo from '../assets/images/logo-navbar.png';
import { LanguageDetector } from '../utils/languageDetection';
import NotificationBell from './NotificationBell';

const LogoWithBanner = () => {
  return (
    <div className="relative h-[75px] w-[133px] ml-2.5">
      <img src={logo || ''} alt="Heartport" className="h-full w-full object-contain" />
      <div className="absolute top-0 right-0 overflow-hidden h-[75px] w-[133px] ml-2.5">
        <div className="absolute top-14 right-[-15px] rotate-45 transform origin-top-right">
          <div className="w-24 text-center py-0.5 bg-red-600 text-white text-[10px] font-bold">
            TEST
          </div>
        </div>
      </div>
    </div>
  );
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isAdminMenuOpen, setIsAdminMenuOpen] = useState(false);
  
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const currentLanguage = useSelector((state: RootState) => state.language.currentLanguage);
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth);
  const [authLoaded, setAuthLoaded] = useState(false);

  const languageDetector = new LanguageDetector({
    defaultLanguage: 'de',
    supportedLanguages: ['de', 'en'],
  });

  useEffect(() => {
    if (isAuthenticated !== undefined) {
      setAuthLoaded(true);
    }
  }, [isAuthenticated]);

  // Prevent background scroll when mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = original; };
    }
  }, [isMenuOpen]);

  const handleLanguageChange = (lang: string) => {
    if (lang === currentLanguage) return;

    dispatch(setLanguage(lang));
    i18n.changeLanguage(lang);
    languageDetector.saveLanguagePreference(lang);

    const newPath = generateLocalizedPath(location.pathname, location.search, lang);
    navigate(newPath);

    setIsMenuOpen(false);
    document.documentElement.lang = lang;
  };

  const generateLocalizedPath = (path: string, search: string, lang: string): string => {
    const pathSegments = path.split('/').filter(Boolean);
    const authRoutes = ['login', 'signup', 'reset-password'];
    if (pathSegments.length > 0 && authRoutes.includes(pathSegments[0])) {
      return path + search;
    }

    if (['de', 'en'].includes(pathSegments[0])) {
      pathSegments.shift();
    }

    if (pathSegments.length === 0) {
      return `/${lang}${search}`;
    }

    return `/${lang}/${pathSegments.join('/')}${search}`;
  };

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate('/login');
      setIsMenuOpen(false);
      setIsProfileMenuOpen(false);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const NavLink: React.FC<{ to: string; children: React.ReactNode }> = ({ to, children }) => {
    const checkIsActive = () => {
      if (to === '/') {
        return location.pathname === `/${currentLanguage}` || location.pathname === '/';
      }

      const pathWithoutLang = location.pathname.replace(/^\/[a-z]{2}\//, '/');
      return pathWithoutLang === to || location.pathname === to;
    };

    const isActive = checkIsActive();
    const baseClasses = 'font-bold text-lg md:text-xl relative';
    const activeClasses = isActive ? 'text-[#47ABDB]' : 'text-white';

    const isAuthRoute = ['/login', '/signup', '/reset-password'].some((route) => to.startsWith(route));
    const localizedTo = isAuthRoute
      ? to
      : to === '/'
        ? `/${currentLanguage}`
        : `/${currentLanguage}${to}`;

    return (
      <span className={`${baseClasses} ${activeClasses} hover:underline`}>
        <Link to={localizedTo} onClick={() => setIsMenuOpen(false)}>{children}</Link>
        <span className="text-[#47ABDB] ml-0.5">.</span>
      </span>
    );
  };

  const LanguageButtons = () => (
    <div className="flex items-center text-xl gap-4">
      <button
        onClick={() => handleLanguageChange('de')}
        className={`font-bold px-2.5 ${currentLanguage === 'de' ? 'text-[#47ABDB]' : 'text-white'} hover:text-[#47ABDB] transition-colors duration-300`}
        aria-label={t('header.switchToGerman')}
      >
        DE
      </button>
      <button
        onClick={() => handleLanguageChange('en')}
        className={`font-bold px-2.5 ${currentLanguage === 'en' ? 'text-[#47ABDB]' : 'text-white'} hover:text-[#47ABDB] transition-colors duration-300`}
        aria-label={t('header.switchToEnglish')}
      >
        EN
      </button>
    </div>
  );

  const ProfileMenu = () => (
    <div className="relative">
      <button
        onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
        className="flex items-center gap-2 font-bold text-white hover:text-[#47ABDB] transition-colors duration-300"
      >
        <User size={20} />
      </button>
      {isProfileMenuOpen && (
        <div className="lg:absolute lg:right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
          <Link
            to={`/${currentLanguage}/settings`}
            className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
            onClick={() => {
              setIsProfileMenuOpen(false);
              setIsMenuOpen(false);
            }}
          >
            {t('settings.title')}
          </Link>
          <button
            onClick={() => {
              handleLogout();
              setIsProfileMenuOpen(false);
              setIsMenuOpen(false);
            }}
            className="flex items-center w-full px-4 py-2 text-red-600 hover:bg-gray-100"
          >
            <LogOut size={16} className="mr-2" />
            {t('nav.signOut')}
          </button>
        </div>
      )}
    </div>
  );

  const showAdmin = user?.role === 'ADMIN';
  const showCloser = user?.role === 'CLOSER';
  const partnerSubmitLabel = t(showAdmin || showCloser ? 'nav.submitLead' : 'nav.partnerSubmitLead');
  const partnerDealsLabel = t(showAdmin || showCloser ? 'nav.deals' : 'nav.partnerDeals');
  const partnerPayoutsLabel = t(showAdmin || showCloser ? 'nav.payouts' : 'nav.partnerPayouts');
  const partnerMenuLabel = t(showAdmin || showCloser ? 'header.partnerMenu' : 'header.ambassadorMenu');

  return (
    <header id="app-header" className="bg-transparent px-4 md:px-6 lg:px-8 py-4">
      <div className="flex justify-between items-center">
        <Link to={`/${currentLanguage}`} className="h-[75px] ml-2.5">
          <LogoWithBanner />
        </Link>
        <nav className="flex items-center">
          <div className={`${isMenuOpen ? 'flex flex-col absolute top-[75px] right-0 max-h-[calc(100vh-75px)] overflow-y-auto bg-[#90C4D9] w-full p-4 shadow-md z-50 gap-6' : 'hidden lg:flex items-center gap-4 xl:gap-8'}`}>
            {(showAdmin || showCloser) ? (
              <>
                <NavLink to="/admin">{t('nav.admin')}</NavLink>
                <NavLink to="/admin/companies">{t('nav.companies')}</NavLink>
                <NavLink to="/admin/deals">{t('nav.deals')}</NavLink>
                {showAdmin && <NavLink to="/admin/payouts">{t('nav.payouts')}</NavLink>}
                {showAdmin && <NavLink to="/admin/users">{t('nav.users')}</NavLink>}
                {showAdmin && <NavLink to="/admin/audit">{t('nav.audit')}</NavLink>}

                {/* Partner dropdown for admins/closers (large screens) */}
                <div className="hidden lg:block relative">
                  <button
                    onClick={() => setIsAdminMenuOpen(!isAdminMenuOpen)}
                    className="font-bold text-lg md:text-xl text-white hover:underline flex items-center gap-1"
                  >
                    {partnerMenuLabel}
                    <span className="text-[#47ABDB]">▼</span>
                  </button>
                  {isAdminMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50">
                      <Link to={`/${currentLanguage}/dashboard`} className="block px-4 py-2 text-gray-700 hover:bg-gray-100" onClick={() => setIsAdminMenuOpen(false)}>
                        {t('nav.dashboard')}
                      </Link>
                      <Link to={`/${currentLanguage}/submit-lead`} className="block px-4 py-2 text-gray-700 hover:bg-gray-100" onClick={() => setIsAdminMenuOpen(false)}>
                        {partnerSubmitLabel}
                      </Link>
                      <Link to={`/${currentLanguage}/companies`} className="block px-4 py-2 text-gray-700 hover:bg-gray-100" onClick={() => setIsAdminMenuOpen(false)}>
                        {t('nav.companies')}
                      </Link>
                      <Link to={`/${currentLanguage}/payouts`} className="block px-4 py-2 text-gray-700 hover:bg-gray-100" onClick={() => setIsAdminMenuOpen(false)}>
                        {partnerPayoutsLabel}
                      </Link>
                      <Link to={`/${currentLanguage}/deals`} className="block px-4 py-2 text-gray-700 hover:bg-gray-100" onClick={() => setIsAdminMenuOpen(false)}>
                        {partnerDealsLabel}
                      </Link>
                    </div>
                  )}
                </div>

                <div className="lg:hidden contents">
                  <NavLink to="/admin">{t('nav.admin')}</NavLink>
                  <NavLink to="/admin/companies">{t('nav.companies')}</NavLink>
                  <NavLink to="/admin/deals">{t('nav.deals')}</NavLink>
                  {showAdmin && <NavLink to="/admin/payouts">{t('nav.payouts')}</NavLink>}
                  {showAdmin && <NavLink to="/admin/users">{t('nav.users')}</NavLink>}
                  {showAdmin && <NavLink to="/admin/audit">{t('nav.audit')}</NavLink>}
                  <div className="w-full border-t border-white my-2"></div>
                  <NavLink to="/dashboard">{t('nav.dashboard')}</NavLink>
                  <NavLink to="/submit-lead">{partnerSubmitLabel}</NavLink>
                  <NavLink to="/companies">{t('nav.companies')}</NavLink>
                  <NavLink to="/payouts">{partnerPayoutsLabel}</NavLink>
                  <NavLink to="/deals">{partnerDealsLabel}</NavLink>
                </div>
              </>
            ) : (
              <>
                <NavLink to="/dashboard">{t('nav.dashboard')}</NavLink>
                <NavLink to="/submit-lead">{partnerSubmitLabel}</NavLink>
                <NavLink to="/companies">{t('nav.companies')}</NavLink>
                <NavLink to="/payouts">{partnerPayoutsLabel}</NavLink>
                <NavLink to="/deals">{partnerDealsLabel}</NavLink>
              </>
            )}

            <div className="lg:hidden w-full border-t border-white my-2"></div>

            <div className={`lg:border-l lg:border-white ${(showAdmin || showCloser) ? 'lg:ml-8 lg:pl-8' : 'lg:ml-16 lg:pl-16'} ${isMenuOpen ? 'flex justify-center' : ''}`}>
              <LanguageButtons />
            </div>
            <div className={`lg:border-l lg:border-white ${(showAdmin || showCloser) ? 'lg:ml-8 lg:pl-8' : 'lg:ml-16 lg:pl-16'} ${isMenuOpen ? 'flex justify-center' : ''}`}>
              {authLoaded && isAuthenticated && (
                <NotificationBell currentLanguage={currentLanguage} isAuthenticated={isAuthenticated} />
              )}
            </div>
            <div className={`lg:border-l lg:border-white ${(showAdmin || showCloser) ? 'lg:ml-8 lg:pl-8' : 'lg:ml-16 lg:pl-16'} ${isMenuOpen ? 'flex justify-center' : ''}`}>
              {authLoaded && (isAuthenticated && user ? <ProfileMenu /> : <NavLink to="/login">{t('nav.signIn')}</NavLink>)}
            </div>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-white focus:outline-none ml-4"
            aria-label={isMenuOpen ? t('header.closeMenu') : t('header.openMenu')}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>
    </header>
  );
};

export default Header;
